import React, { useContext } from "react";
import TotalBalanceGreen from "../../svg/TotalBalanceGreen";
import TotalBalanceOrange from "../../svg/TotalBalanceOrange";
import TotalBalanceGrey from "../../svg/TotalBalanceGrey";
import TotalBalanceYellow from "../../svg/TotalBalanceYellow";
import LanguageContext from "../../../utils/Contexts/LanguageContext";
import Constant from "../../Constants";
import ThemeContext from "../../../utils/Contexts/ThemeContext";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import UserContext from "../../../utils/Contexts/UserContext";
import TotalBalanceRed from "../../svg/TotalBalanceRed";

ChartJS.register(ArcElement, Tooltip, Legend);

const getRatingLabel = (rating) => {
  if (rating >= 4) return "Excellent!";
  if (rating >= 3) return "Good!";
  if (rating >= 2) return "Fair!";
  return "Poor!";
};

function SellerRating() {
  const { t, language } = useContext(LanguageContext);
  const { themeStatus } = Constant();
  const { selectedTheme } = useContext(ThemeContext);
  const { userData } = useContext(UserContext);
  const rating = Math.min(userData?.rating || 0, 5);

  const totalSegments = 10;
  const filledSegments = Math.round((rating / 5) * totalSegments);
  const gradientStops = [
    [
      [0, "rgba(237, 76, 120, 1)"],
      [1, "rgba(237, 76, 120, 1)"],
    ],
    [
      [0, "rgba(237, 76, 120, 1)"],
      [1, "rgba(237, 76, 120, 1)"],
    ],
    [
      [0, "rgba(237, 76, 120, 1)"],
      [1, "rgba(237, 76, 120, 1)"],
    ],
    [
      [1, "#ed5d6b"],
      [0.7, "#ed6b60"],
    ],
    [
      [0, "rgba(237, 76, 120, 1)"],
      [0.3, "rgba(237, 76, 120, 1)"],
      [1, "rgba(237, 134, 76, 1)"],
    ],
    [
      [0, "rgba(237, 134, 76, 1)"],
      [1, "rgba(237, 134, 76, 1)"],
    ],
    [
      [0.99, "rgba(237, 134, 76, 1)"],
      [0, "rgb(150, 138, 96)"],
      [0.7, "rgba(250, 199, 16, 1)"],
    ],
    [
      [0, "rgba(250, 199, 16, 1)"],
      [1, "rgba(250, 199, 16, 1)"],
    ],
    [
      [0.99, "rgba(250, 199, 16, 1)"],
      [0, "rgba(2, 201, 166, 1)"],
      [0.1, "rgba(2, 201, 166, 1)"],
    ],
    [
      [0, "rgba(2, 201, 166, 1)"],
      [0.01, "#51c875"],
      [1, "rgba(2, 201, 166, 1)"],
    ],
  ];

  // Gradient generator
  function createLinearGradient(ctx, p1, p2, colorStops) {
    const gradient = ctx.createLinearGradient(p1.x, p2.x, p1.y, p2.y);
    colorStops.forEach(([pos, color]) => gradient.addColorStop(pos, color));
    return gradient;
  }

  // Plugin to apply custom linear gradients to each arc
  const gradientSegmentPlugin = {
    id: "gradientSegment",
    beforeDatasetDraw(chart) {
      const ctx = chart.ctx;
      const datasetMeta = chart.getDatasetMeta(0);
      const center = {
        x: chart.chartArea.left + chart.chartArea.width / 2,
        y: chart.chartArea.top + chart.chartArea.height / 2,
      };

      datasetMeta.data.forEach((arc, i) => {
        const angle = (arc.startAngle + arc.endAngle) / 2;
        const radius = arc.outerRadius;
        const x = center.x + radius * Math.cos(angle);
        const y = center.y + radius * Math.sin(angle);

        const gradient = createLinearGradient(
          ctx,
          center,
          { x, y },
          gradientStops[i]
        );
        arc.options.backgroundColor = gradient;
      });
    },
  };

  const data = {
    labels: Array(totalSegments).fill(""),
    datasets: [
      {
        data: Array(totalSegments).fill(1),
        backgroundColor: Array(totalSegments).fill("#ccc"), // will be overridden
        borderWidth: 4,
        borderColor: selectedTheme === "dark" ? "#25282a" : "#FFFFFF99",
        cutout: "85%",
        borderRadius: 20,
      },
    ],
  };

  const centerTextPlugin = {
    id: "centerText",
    beforeDraw(chart) {
      const { width, height } = chart;
      const ctx = chart.ctx;

      const container =
        document.querySelector(`.${themeStatus[selectedTheme]}`) ||
        document.documentElement;

      // Resolve theme-aware color
      const bgColor =
        getComputedStyle(container).getPropertyValue("--graph-bg").trim() ||
        "rgba(45, 50, 54, 1)";

      const centerX = width / 2;
      const centerY = height / 1.95;
      const innerRadius = chart._metasets[0].data[0].innerRadius * 0.9;

      ctx.save();
      ctx.beginPath();
      ctx.arc(centerX, centerY, innerRadius, 0, 2 * Math.PI);
      ctx.fillStyle = bgColor;
      ctx.fill();

      const ratingText = rating.toFixed(2);
      const labelText = getRatingLabel(rating);

      ctx.font = "bold 36px sans-serif";
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(ratingText, centerX, centerY - 10);

      ctx.font = "16px sans-serif";
      ctx.fillStyle = "#6C747A";
      ctx.fillText(labelText, centerX, centerY + 16);

      ctx.restore();
    },
  };

  const pointerRingPlugin = {
    id: "pointerRing",
    afterDatasetDraw(chart) {
      const dataset = chart.getDatasetMeta(0);
      const segment = dataset.data[filledSegments - 1];
      if (!segment) return;

      const { x, y } = segment.tooltipPosition();
      const ctx = chart.ctx;
      const container =
        document.querySelector(`.${themeStatus[selectedTheme]}`) ||
        document.documentElement;

      // Resolve theme-aware color
      const bgColor =
        getComputedStyle(container).getPropertyValue("--graph-bg").trim() ||
        "rgba(45, 50, 54, 1)";

      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, 11, 0, 2 * Math.PI);
      ctx.lineWidth = 5;
      ctx.strokeStyle = "#ffd166";
      ctx.stroke();
      ctx.fillStyle = bgColor;
      ctx.fill();
      ctx.restore();
    },
  };

  const options = {
    responsive: true,
    rotation: -160,
    circumference: 320,
    animation: {
      animateRotate: false,
      animateScale: false,
    },
    hover: {
      mode: null,
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      gradientSegment: true,
    },
  };
  return (
    <div
      className={`w-100 p-3 rounded seller-rating-height cardSetting ${themeStatus?.[selectedTheme]}`}
      style={{ backgroundColor: "var(--bg-card)" }}
    >
      <div
        className="d-flex align-items-center"
        style={{ paddingBlock: "0.4rem" }}
      >
        <h4
          className="dashboard-title-responsive p-0 mb-0"
          style={{ lineHeight: "150%" }}
        >
          {t("Seller Rating")}
        </h4>
      </div>
      <div
        className="my-3"
        style={{
          borderBottom: `2px solid var(--border)`,
          marginLeft: "-18px",
          marginRight: "-18px",
        }}
      ></div>
      <div className="d-flex seller-rating">
        <div id="chartDiv" className="d-flex seller-doughnut">
          <Doughnut
            key={`${selectedTheme}-${language}`}
            data={data}
            options={options}
            plugins={[
              centerTextPlugin,
              pointerRingPlugin,
              gradientSegmentPlugin,
            ]}
          />
        </div>

        <div
          className="flex-column gap-4 seller-rating-labels-sm"
          style={{ marginTop: "0.5rem" }}
        >
          <div className="totalBalanceLabel">
            <TotalBalanceGrey fill={"var(--graph-bg)"} /> {t("Not Calculated")}
          </div>
          <div className="totalBalanceLabel">
            <TotalBalanceRed /> {t("Poor")}
          </div>
          <div className="totalBalanceLabel">
            <TotalBalanceOrange /> {t("Fair")}
          </div>
          <div className="totalBalanceLabel">
            <TotalBalanceYellow /> {t("Good")}
          </div>
          <div className="totalBalanceLabel">
            <TotalBalanceGreen /> {t("Excellent")}
          </div>
        </div>

        <div className="seller-rating-labels-lg w-100 align-items-center gap-3">
          <div className="d-flex justify-content-center w-100">
            <div
              className="totalBalanceLabel d-flex justify-content-center"
              style={{ width: "33%" }}
            >
              <TotalBalanceGrey /> {t("Not Calculated")}
            </div>
            <div
              className="totalBalanceLabel d-flex justify-content-center"
              style={{ width: "33%" }}
            >
              <TotalBalanceRed /> {t("Poor")}
            </div>
            <div
              className="totalBalanceLabel d-flex justify-content-center"
              style={{ width: "33%" }}
            >
              <TotalBalanceOrange /> {t("Fair")}
            </div>
          </div>
          <div className="d-flex w-100 justify-content-center gap-1 mt-1">
            <div
              className="totalBalanceLabel d-flex justify-content-center"
              style={{ width: "33%" }}
            >
              <TotalBalanceYellow /> {t("Good")}
            </div>
            <div
              className="totalBalanceLabel d-flex justify-content-center"
              style={{ width: "33%" }}
            >
              <TotalBalanceGreen /> {t("Excellent")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SellerRating;
